import React, { useEffect, useState } from "react";
import TopNav from "./components/TopNav";
import FilterBar from "./components/FilterBar";
import SentimentPanel from "./components/SentimentPanel";
import useCache from "./hooks/useCache";
import { handleWebfuse, initializeWebfuseSession } from "./utils/webfuse";
import { fetchNews, fetchNewsUsage } from "./utils/newsApi";
import HeadlineList from "./components/HeadlineList";
import { useUXContext } from "./components/UXContext";

export default function Home() {
  const [sessionId, setSessionId] = useState(null);

  const {
    articles,
    setArticles,
    selected,
    setSelected,
    filters,
    setFilters,
    clearAllCache,
  } = useCache();

  const { mode, setMode, leftPanelOpen, closeLeftPanel } = useUXContext();

  useEffect(() => {
    const cleanup = initializeWebfuseSession(setSessionId);
    return cleanup;
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === "reload_webfuse_iframe") {
        console.log("reload_webfuse_iframe message received: ", event);
        const iframe = document.getElementById("news-reader-frame");
        if (iframe) {
          iframe.src = iframe.src; // Triggers reload
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const handleArticleClick = (articleOrUrl) => {
    const url =
      typeof articleOrUrl === "string" ? articleOrUrl : articleOrUrl?.url;

    if (url) {
      if (typeof articleOrUrl === "object") {
        setSelected(articleOrUrl);
      }
      // Just pass the URL to Webfuse
      handleWebfuse(url);
      // Close the left panel when an article is chosen
      closeLeftPanel();
    } else {
      console.warn("❌ Invalid article object or URL passed to Webfuse.");
    }
  };

  const handleSearch = async () => {
    fetchNews({ mode, filters, setArticles, setSelected });
  };

  return (
    <>
      <TopNav
        fetchNewsUsage={fetchNewsUsage}
        onClearCache={clearAllCache}
        onSubmitUrl={handleArticleClick}
      />

      <div
        className="h-screen w-full fixed top-0 left-0 flex font-sans"
        style={{ paddingTop: "var(--nav-height)" }}
      >
        {/* Make this relative so the off-canvas left panel and backdrop can be absolutely positioned inside */}
        <div
          className="flex flex-1 overflow-hidden relative"
          style={{ "--left-panel-w": "420px" }}
        >
          {/* LEFT PANEL — off-canvas overlay; never unmounted */}
          <aside
            className="left-panel flex flex-col border-r border-gray-800"
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "var(--left-panel-w)", // single source of truth
              backgroundColor: "var(--color-bg-base)",
              transform: leftPanelOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 240ms ease",
              zIndex: 50,
              // While hidden, don't block clicks to content underneath
              pointerEvents: leftPanelOpen ? "auto" : "none",
            }}
          >
            {/* Fixed FilterBar */}
            <div className="sticky z-10 top-[var(--topnav-height)] bg-[var(--color-bg-base)] p-2 pb-0">
              <FilterBar
                filters={filters}
                setFilters={setFilters}
                onSearch={handleSearch}
                mode={mode}
                setMode={setMode}
              />
            </div>

            {/* Scrollable HeadlineList */}
            <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
              <HeadlineList
                articles={mode === "url" ? [] : articles}
                setArticles={setArticles}
                handleArticleClick={handleArticleClick}
                selectedArticle={selected}
              />
            </div>
          </aside>

          {/* BACKDROP covers the whole content area; click to close */}
          <div
            className="absolute top-0 right-0 bottom-0"
            onClick={closeLeftPanel}
            style={{
              left: 0, // cover full width under the panel
              background: "rgba(4,12,23,0.82)",
              opacity: leftPanelOpen ? 1 : 0,
              transition: "opacity 200ms ease",
              pointerEvents: leftPanelOpen ? "auto" : "none",
              zIndex: 40, // below left panel (50), above content
            }}
          />

          {/* CENTER */}
          <main className="flex-1 min-w-0 bg-white p-0 overflow-hidden relative">
            <iframe
              id="news-reader-frame"
              src="https://webfuse.com/+news-reader/"
              title="News Reader"
              className="w-full h-full border-0"
            ></iframe>
          </main>

          {/* RIGHT — always visible */}
          <aside
            className="w-[700px] text-white flex flex-col h-full"
            style={{ backgroundColor: "#040c17" }}
          >
            <SentimentPanel />
          </aside>
        </div>
      </div>
    </>
  );
}
